import { formatAmount } from '../../../util';
import {
  PriceTable,
  bidPrices,
  bidTotal,
  enteredValuesOf,
  latestBidVersion,
  priceTableOf,
} from '../../../util/bidTotal';
import { PriceRows } from '../../../models/priceRows.model';
import { QuotationVersion } from '../../../models/quotationVersion.model';

type AreaSupplier = {
  supplierId: { _id: string; name: string };
};

type ProjectArea = {
  areaId: { _id: string };
  suppliers: AreaSupplier[];
  budget?: string;
};

type PurchaseVersionData = {
  areaId: string;
  versionData: QuotationVersion[];
  failed?: boolean;
};

export type PurchaseBid = {
  supplierId: string;
  supplierName: string;
  amount: number;
  unit?: string;
  bidDate?: string;
};

export type BidSpread = {
  lowest: number;
  highest: number;
  average: number;
  unit?: string;
};

export const latestBids = (
  versions: QuotationVersion[],
  areaSuppliers: AreaSupplier[],
  table: PriceTable,
  fallbackUnit: string,
): PurchaseBid[] => {
  const bids: PurchaseBid[] = [];
  for (const { supplierId: supplier } of areaSuppliers) {
    const version = latestBidVersion(versions, supplier._id);
    if (!version) continue;

    const prices = bidPrices(versions, version.refVersion, supplier._id, fallbackUnit);
    const total = bidTotal(table, prices, supplier._id, version.refVersion);
    bids.push({
      supplierId: supplier._id,
      supplierName: supplier.name,
      amount: total?.amount ?? 0,
      unit: total?.unit,
      bidDate: version.supplierBids?.find(b => b.supplierId === supplier._id)?.bidDate,
    });
  }
  return bids;
};

export const bidsByPurchase = (
  purchaseVersionData: PurchaseVersionData[],
  projectPriceRows: PriceRows[],
  areas: ProjectArea[],
  fallbackUnit: string,
): Map<string, PurchaseBid[]> => {
  const bids = new Map<string, PurchaseBid[]>();
  for (const area of areas) {
    const areaId = area.areaId._id;
    const versions = purchaseVersionData.find(entry => entry.areaId === areaId);
    const priceRows = projectPriceRows.find(entry => entry.areaId === areaId);
    if (!versions || versions.failed || !priceRows) continue;

    const table = priceTableOf(priceRows.rows, enteredValuesOf(priceRows.values), area.budget);
    bids.set(areaId, latestBids(versions.versionData, area.suppliers, table, fallbackUnit));
  }
  return bids;
};

export const compareBidCounts = (
  bids: Map<string, PurchaseBid[]>,
  areaIdA: string,
  areaIdB: string,
) => (bids.get(areaIdA)?.length ?? 0) - (bids.get(areaIdB)?.length ?? 0);

export const spreadOf = (bids: PurchaseBid[]): BidSpread | undefined => {
  const unit = bids.map(bid => bid.unit).find(Boolean);
  // A bid without any prices has no unit, so only the units that exist must agree.
  if (!bids.length || bids.some(bid => bid.unit && bid.unit !== unit)) return undefined;
  const amounts = bids.map(bid => bid.amount);
  return {
    lowest: Math.min(...amounts),
    highest: Math.max(...amounts),
    average: amounts.reduce((total, amount) => total + amount, 0) / amounts.length,
    unit,
  };
};

export const amountText = (amount: number) => formatAmount(amount) || '0';

export const amountWithUnit = (amount: number, unit?: string) => [amountText(amount), unit].filter(Boolean).join(' ');

export const percentDifference = (amount: number, reference: number) =>
  reference === 0 ? 0 : Math.round(((amount - reference) / reference) * 100);

export type BidRank =
  | { kind: 'lowest' }
  | { kind: 'highest' }
  | { kind: 'between'; position: number; count: number };

export const rankOf = (amount: number, bids: PurchaseBid[]): BidRank => {
  const amounts = bids.map(bid => bid.amount);
  if (amount === Math.min(...amounts)) return { kind: 'lowest' };
  if (amount === Math.max(...amounts)) return { kind: 'highest' };
  const cheaper = amounts.filter(other => other < amount).length;
  return { kind: 'between', position: cheaper + 1, count: amounts.length };
};
